import sortBy from 'lodash/sortBy';

import {baseComparator, boolComparator} from 'common/util/array';
import {getIconForEvent} from 'common/report/util/events';
import {lexicalCompose} from 'common/util/function';
import {getNonAutotrackCustomEvents, hasUserFilters} from 'common/util/custom_events';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {SPECIAL_EVENTS} from 'common/report/constants';
import {EventDefinition} from 'common/util/definitions-types';

import {EventItem} from './events-menu/types';
import {PropertyItem} from './properties-menu/types';
import {CohortSegment} from './segment-menu/types';

function baseSeachableContent(eventDefinition: EventDefinition): string[] {
  return [eventDefinition?.description, ...(eventDefinition?.tags || []), ...(eventDefinition?.platforms || [])].filter(
    Boolean,
  );
}

/**
 * Prepare one custom event entry for display in mp-items-menu
 * @param {object} customEvent - custom event from query response
 * @return {object} - object compatible with mp-items-menu component
 */
export function getCustomEventMenuItem(customEvent, eventDefinition?: EventDefinition) {
  return {
    ...customEvent,
    custom: true,
    isCollectEverything: customEvent.is_collect_everything_event,
    icon: getIconForEvent({...customEvent, custom: true}),
    label: customEvent.name,
    value: `$custom_event:${customEvent.id}`,
    searchableContent: baseSeachableContent(eventDefinition).join(` `),
  };
}

/**
 * Prepare one top event entry for display in mp-items-menu
 * @param {object} topEvent - top event from query response
 * @return {object} - object compatible with mp-items-menu component
 */
export function getEventMenuItem(topEvent, eventDefinition?: EventDefinition) {
  return {
    ...topEvent,
    icon: getIconForEvent(topEvent),
    value: topEvent.name,
    searchableContent: [...baseSeachableContent(eventDefinition), eventDefinition?.name].join(` `),
  };
}

export function getDataDefinitionsEventMenuItem(eventDefinition: EventDefinition) {
  return {
    ...eventDefinition,
    icon: getIconForEvent(eventDefinition),
    label: eventDefinition.displayName?.length > 0 ? eventDefinition.displayName : eventDefinition.name,
    value: eventDefinition.name,
    searchableContent: [...baseSeachableContent(eventDefinition), eventDefinition?.name].join(` `),
  };
}

/**
 * Builds cohort definitions from a list of cohorts
 */
export function getCohortsDefinitions(cohorts) {
  return sortBy(
    (cohorts || []).map((cohort) => ({
      label: cohort.name,
      value: cohort.id,
      name: cohort.name,
      count: cohort.count,
      description: cohort.description,
      createdBy: cohort.created_by,
      lastEdited: cohort.last_edited,
      can_view: cohort.can_view,
      can_update_basic: cohort.can_update_basic,
    })),
    `label`,
  );
}

export const orderByLabel = lexicalCompose(
  boolComparator((item) => item.name === SPECIAL_EVENTS.TOP_EVENTS), // Top Events first
  baseComparator({transform: (item) => item.label && item.label.toLowerCase()}),
);

export function getCohortFromPropertyItem(item: PropertyItem): CohortSegment {
  let cohort = Number.isInteger(item.value) ? {id: item.value} : item.value.cohort;
  const isInlineCohort = cohort && !!cohort.raw_cohort;

  if (isInlineCohort) {
    cohort = {raw_cohort: {name: cohort.name || item.label, ...cohort.raw_cohort}};
  } else {
    cohort = {...(item.dataGroupId ? {data_group_id: item.dataGroupId} : {}), ...cohort};
  }

  return {cohort: {...cohort, name: item.label, negated: false}};
}

export function getCustomEventItemsFiltered({
  customEventsStoreValues,
  eventDefinitionsStoreValues,
  excludeAutotrackEvents,
  dataGroupId,
  eventData,
}: {
  customEventsStoreValues: CustomEventDefinition;
  eventDefinitionsStoreValues: {[eventName: string]: EventDefinition};
  excludeAutotrackEvents: boolean;
  dataGroupId: string;
  eventData: EventItem[];
}) {
  let customEvents = Object.values(customEventsStoreValues)
    // entity-sharing : hide custom events that user does not have access to from menus
    .filter((ce) => ce.can_view)
    .map((ce) => {
      return getCustomEventMenuItem(ce, eventDefinitionsStoreValues[ce.name]);
    });

  if (dataGroupId) {
    customEvents = customEvents.filter((ev) => !hasUserFilters(ev, customEvents));
  }

  if (excludeAutotrackEvents) {
    customEvents = getNonAutotrackCustomEvents(customEvents, eventData);
  }

  return customEvents;
}
